import React, { useState, useEffect, useRef } from 'react';
import {
    FaUserCircle, FaStar, FaIdCard, FaHome,
    FaUserTie, FaBell, FaPhoneAlt, FaEnvelope,
    FaMobileAlt, FaTools, FaCheckCircle, FaCamera, FaPencilAlt,
    FaClock, FaHardHat, FaHistory, FaSave, FaTimes, FaPlusCircle, FaUtensils,
    FaFileInvoiceDollar, FaCalendarDay
} from 'react-icons/fa';
import pb from '../../lib/pocketbase';
import { toast } from 'react-toastify';
import styles from '../../pages/Account.module.css';

const TabProfile = ({ user, userRole, rating, setRating, comment, setComment, handleReviewSubmit, bonuses, allTimeStats, projects, userExpenses }) => {
    const isVisitor = user?.role === 'user' || user?.profile_type === 'visitor';
    const isWorker = user?.role === 'worker' || user?.role === 'foreman';
    const fileInputRef = useRef(null);

    // Seniority helper
    const getSeniority = () => {
        const now = new Date();
        const createdDate = new Date(user.created);
        const diffTime = Math.abs(now - createdDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 365) return `${Math.floor(diffDays / 365)} an(s)`;
        if (diffDays > 30) return `${Math.floor(diffDays / 30)} mois`;
        return `${diffDays} jours`;
    };

    // Editing States
    const [editingPhone, setEditingPhone] = useState(false);
    const [tempPhone, setTempPhone] = useState(user?.phone || '');
    const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(user?.avatar ? pb.files.getURL(user, user.avatar) : null);

    // 2. Avatar Update
    const handleAvatarClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setIsUpdatingAvatar(true);
            const formData = new FormData();
            formData.append('avatar', file);

            const updatedUser = await pb.collection('users').update(user.id, formData);
            setPreviewUrl(pb.files.getURL(updatedUser, updatedUser.avatar));
            toast.success("Photo de profil mise à jour !");
        } catch (error) {
            toast.error("Erreur lors de la mise à jour de la photo");
        } finally {
            setIsUpdatingAvatar(false);
        }
    };

    // 3. Fluid Editing Update
    const handleUpdateField = async (field, value) => {
        try {
            await pb.collection('users').update(user.id, { [field]: value });
            toast.success("Informations enregistrées !");
            if (field === 'phone') setEditingPhone(false);
        } catch (error) {
            toast.error("Erreur lors de l'enregistrement");
        }
    };

    return (
        <div className={styles.tabContent}>
            {/* PROFILE HEADER / AVATAR */}
            <div className={styles.greetingCard} style={{
                background: 'linear-gradient(135deg, var(--color-primary), #1e40af)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '30px',
                padding: '40px'
            }}>
                <div style={{ position: 'relative', cursor: 'pointer' }} onClick={handleAvatarClick}>
                    <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        border: '5px solid rgba(255,255,255,0.2)',
                        overflow: 'hidden',
                        background: 'rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                        transition: 'transform 0.3s ease'
                    }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        {previewUrl ? (
                            <img src={previewUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <FaUserCircle size={80} color="rgba(255,255,255,0.5)" />
                        )}
                        {isUpdatingAvatar && (
                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div className={styles.spinner}></div>
                            </div>
                        )}
                    </div>
                    <button style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px',
                        background: 'white',
                        color: 'var(--color-primary)',
                        border: 'none',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        fontSize: '0.9rem'
                    }}>
                        <FaCamera />
                    </button>
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} accept="image/*" />
                </div>

                <div>
                    <h2 className={styles.greetingTitle} style={{ color: 'white', marginBottom: '8px' }}>
                        Bonjour, {user.name?.split(' ')[0] || 'Artisan'} 👋
                    </h2>
                    <p className={styles.greetingSubtitle} style={{ color: 'rgba(255,255,255,0.9)' }}>
                        <FaHardHat style={{ marginRight: '8px' }} />
                        {(() => {
                            const labels = {
                                'admin': 'Administrateur',
                                'foreman': 'Chef de Chantier',
                                'worker': 'Artisan Terrain',
                                'employee': 'Bureau / Gestion',
                                'user': 'Visiteur',
                                'client': 'Client',
                                'candidate': 'Candidat'
                            };
                            return labels[userRole] || userRole;
                        })()}
                    </p>
                </div>
            </div>

            {/* MINI STATS CARDS */}
            {isWorker && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '20px',
                    marginTop: '-25px',
                    padding: '0 25px',
                    marginBottom: '30px'
                }}>
                    <div className={styles.card} style={{ textAlign: 'center', padding: '15px', borderTop: '4px solid #14b8a6' }}>
                        <FaCalendarDay style={{ color: '#14b8a6', fontSize: '1.2rem', marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{bonuses?.weeklyHours || 0}h / 39h</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>Cette Semaine</div>
                    </div>
                    <div className={styles.card} style={{ textAlign: 'center', padding: '15px', borderTop: '4px solid #8b5cf6' }}>
                        <FaClock style={{ color: '#8b5cf6', fontSize: '1.2rem', marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{bonuses?.monthlyHours || 0}h</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>Ce mois-ci</div>
                    </div>
                    <div className={styles.card} style={{ textAlign: 'center', padding: '15px', borderTop: '4px solid #3b82f6' }}>
                        <FaTools style={{ color: '#3b82f6', fontSize: '1.2rem', marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{projects.length}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>Chantiers impactés</div>
                    </div>
                    <div className={styles.card} style={{ textAlign: 'center', padding: '15px', borderTop: '4px solid #10b981' }}>
                        <FaHistory style={{ color: '#10b981', fontSize: '1.2rem', marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{allTimeStats?.totalHours || 0}h</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>Depuis toujours</div>
                    </div>
                    <div className={styles.card} style={{ textAlign: 'center', padding: '15px', borderTop: '4px solid #f59e0b' }}>
                        <FaPlusCircle style={{ color: '#f59e0b', fontSize: '1.2rem', marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>+{allTimeStats?.totalOvertime || 0}h</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>Heures Sup Total</div>
                    </div>
                    <div className={styles.card} style={{ textAlign: 'center', padding: '15px', borderTop: '4px solid #ec4899' }}>
                        <FaFileInvoiceDollar style={{ color: '#ec4899', fontSize: '1.2rem', marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{userExpenses?.reduce((acc, exp) => acc + (parseFloat(exp.amount) || 0), 0).toFixed(2)}€</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>Notes de Frais</div>
                    </div>
                    <div className={styles.card} style={{ textAlign: 'center', padding: '15px', borderTop: '4px solid #ef4444' }}>
                        <FaUtensils style={{ color: '#ef4444', fontSize: '1.2rem', marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{allTimeStats?.totalLunch || 0}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>Paniers Repas</div>
                    </div>
                    <div className={styles.card} style={{ textAlign: 'center', padding: '15px', borderTop: '4px solid #64748b' }}>
                        <FaUserCircle style={{ color: '#64748b', fontSize: '1.2rem', marginBottom: '8px' }} />
                        <div style={{ fontSize: '1.2rem', fontWeight: '800' }}>{getSeniority()}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>Ancienneté</div>
                    </div>
                </div>
            )}

            {isVisitor && (
                <div className={styles.card}>
                    <h3 className={styles.cardTitle}><FaIdCard /> Votre Profil</h3>
                    <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', marginBottom: '20px' }}>
                        Précisez vos intentions pour nous aider à mieux vous accompagner.
                    </p>
                    <div className={styles.identityGrid}>
                        <div
                            className={`${styles.identityCard} ${user.profile_type === 'client' ? styles.identityCardActive : ''}`}
                            onClick={() => handleUpdateField('profile_type', 'client')}
                        >
                            <div className={styles.identityIcon}><FaHome /></div>
                            <div className={styles.identityInfo}>
                                <h4>Futur Propriétaire</h4>
                                <p>Je souhaite construire ou rénover.</p>
                            </div>
                        </div>
                        <div
                            className={`${styles.identityCard} ${user.profile_type === 'candidate' ? styles.identityCardActive : ''}`}
                            onClick={() => handleUpdateField('profile_type', 'candidate')}
                        >
                            <div className={styles.identityIcon}><FaUserTie /></div>
                            <div className={styles.identityInfo}>
                                <h4>Candidat</h4>
                                <p>Je souhaite rejoindre l'équipe.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '25px' }}>
                <div className={styles.card}>
                    <h3 className={styles.cardTitle}><FaUserCircle /> Coordonnées</h3>
                    <div className={styles.profileInfo}>
                        <div className={styles.infoRow}>
                            <strong>Nom complet</strong>
                            <span>{user.name || 'Non renseigné'}</span>
                        </div>
                        <div className={styles.infoRow}>
                            <strong>Email</strong>
                            <span>{user.email}</span>
                        </div>
                        <div className={styles.infoRow}>
                            <strong>Tél</strong>
                            {editingPhone ? (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        value={tempPhone}
                                        onChange={e => setTempPhone(e.target.value)}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            border: '1px solid var(--color-primary)',
                                            width: '140px'
                                        }}
                                    />
                                    <button onClick={() => handleUpdateField('phone', tempPhone)} style={{ color: '#10b981', border: 'none', background: 'none', cursor: 'pointer' }}><FaSave /></button>
                                    <button onClick={() => { setEditingPhone(false); setTempPhone(user.phone || ''); }} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}><FaTimes /></button>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span>{user.phone || 'Non renseigné'}</span>
                                    <button
                                        onClick={() => setEditingPhone(true)}
                                        style={{ color: '#94a3b8', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem' }}
                                    >
                                        <FaPencilAlt />
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className={styles.infoRow}>
                            <strong>Rôle</strong>
                            <span className={styles.capitalize} style={{ color: 'var(--color-primary)', fontWeight: '800' }}>
                                {(() => {
                                    const labels = {
                                        'admin': 'Administrateur',
                                        'foreman': 'Chef de Chantier',
                                        'worker': 'Artisan Terrain',
                                        'employee': 'Bureau / Gestion',
                                        'user': 'Visiteur',
                                        'client': 'Client',
                                        'candidate': 'Candidat'
                                    };
                                    return labels[userRole] || userRole;
                                })()}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={styles.card}>
                    <h3 className={styles.cardTitle}><FaBell /> Préférences de Contact</h3>
                    <p className={styles.cardSubtitle}>
                        Comment souhaitez-vous être contacté ?
                    </p>
                    <div className={styles.preferenceGrid}>
                        <div
                            className={`${styles.preferenceItem} ${user.contact_pref === 'email' ? styles.preferenceItemActive : ''}`}
                            onClick={() => handleUpdateField('contact_pref', 'email')}
                        >
                            <FaEnvelope style={{ marginRight: '8px' }} /> Email
                        </div>
                        <div
                            className={`${styles.preferenceItem} ${user.contact_pref === 'phone' ? styles.preferenceItemActive : ''}`}
                            onClick={() => handleUpdateField('contact_pref', 'phone')}
                        >
                            <FaPhoneAlt style={{ marginRight: '8px' }} /> Téléphone
                        </div>
                        <div
                            className={`${styles.preferenceItem} ${user.contact_pref === 'sms' ? styles.preferenceItemActive : ''}`}
                            onClick={() => handleUpdateField('contact_pref', 'sms')}
                        >
                            <FaMobileAlt style={{ marginRight: '8px' }} /> SMS / WhatsApp
                        </div>
                    </div>

                    <div className={styles.pushNotifBox}>
                        <FaCheckCircle color="#10b981" style={{ marginTop: '3px', flexShrink: 0 }} />
                        <div>
                            <span className={styles.pushNotifTitle}>Notifications Push</span>
                            <span className={styles.pushNotifSub}>Activées sur cet appareil</span>
                        </div>
                    </div>
                </div>
            </div>

            {userRole === 'client' && (
                <div className={styles.reviewSection} style={{ marginTop: '1rem' }}>
                    <div className={styles.card}>
                        <h3 className={styles.cardTitle}><FaStar /> Votre avis sur nos services</h3>
                        <form onSubmit={handleReviewSubmit}>
                            <div className={styles.formGroup}>
                                <label htmlFor="rating">Note de satisfaction (1 à 5)</label>
                                <input
                                    type="number"
                                    id="rating"
                                    value={rating}
                                    onChange={(e) => setRating(e.target.value)}
                                    min="1"
                                    max="5"
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor="comment">Votre témoignage</label>
                                <textarea
                                    id="comment"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows="4"
                                    placeholder="Partagez votre expérience avec nous..."
                                />
                            </div>
                            <button type="submit" className={styles.submitButton}>Publier mon avis</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabProfile;
